"use client";

import { useState, useRef, useEffect } from "react";
import {
  Camera,
  ImageIcon,
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  ListOrdered,
  Link2,
  Quote,
  Eye,
  EyeOff,
  Save,
  X,
  ChevronDown,
  Check,
  FileText,
  Calendar,
  Code,
  Strikethrough,
  Undo,
  Redo,
  Menu,
} from "lucide-react";
import RichTextEditor from "../components/gallyonomics_components/RichText";
import { errorToast, successToast } from "../utils/toasters";
import { handlePostRequest } from "../hooks/api";
import {
  createGallyoSentimentArticle,
  createMarketPulseArticle,
  getMarketPulseArticle,
  listMarketPulseArticle,
} from "../services/article";

// Mock data for categories and tags
const mockCategories = [
  { id: "1", name: "Market Pulse" },
  { id: "2", name: "Gallyo Sentiment" },
];

function Button({
  children,
  variant = "default",
  className = "",
  onClick,
  disabled,
  size = "default",
  ...props
}) {
  const baseClasses =
    "rounded-md font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";

  const sizeClasses = {
    sm: "px-3 py-1.5 text-sm",
    default: "px-4 py-2",
    lg: "px-6 py-3 text-lg",
  };

  const variants = {
    default: "bg-gray-200 text-gray-800 hover:bg-gray-300 focus:ring-gray-500",
    primary:
      "bg-purple-600 text-white hover:bg-purple-700 focus:ring-purple-500 shadow-sm hover:shadow-md",
    outline:
      "border border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-gray-500 bg-white",
    secondary:
      "bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-gray-500",
    ghost: "text-gray-700 hover:bg-gray-100 focus:ring-gray-500",
  };

  return (
    <button
      className={`${baseClasses} ${sizeClasses[size]} ${variants[variant]} ${className}`}
      onClick={onClick}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
}

function Popover({ children, content, open, onOpenChange, align = "start" }) {
  const popoverRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        onOpenChange(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, onOpenChange]);

  return (
    <div className="relative">
      {children}
      {open && (
        <div
          ref={popoverRef}
          className={`absolute top-full z-50 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg ${
            align === "end" ? "right-0" : "left-0"
          } max-w-[calc(100vw-2rem)] sm:max-w-none`}
        >
          {content}
        </div>
      )}
    </div>
  );
}

function ImagePickerModal({
  isOpen,
  onClose,
  onImageSelected,
  mode = "upload",
}) {
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      onImageSelected(file);
      onClose();
    }
  };

  const handleCameraCapture = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-4 sm:p-6 max-w-md w-full shadow-xl">
        <div className="flex justify-between items-center mb-4 sm:mb-6">
          <h3 className="text-lg font-semibold text-gray-900">
            Select Cover Image
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-3">
          <Button
            variant="outline"
            className="w-full flex items-center gap-3 justify-center py-3 hover:bg-gray-50 bg-transparent"
            onClick={handleCameraCapture}
          >
            <Camera size={20} />
            Take Photo
          </Button>

          <Button
            variant="outline"
            className="w-full flex items-center gap-3 justify-center py-3 hover:bg-gray-50 bg-transparent"
            onClick={() => fileInputRef.current?.click()}
          >
            <ImageIcon size={20} />
            Upload from Gallery
          </Button>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            capture={mode === "camera" ? "environment" : undefined}
          />
        </div>
      </div>
    </div>
  );
}

function CategoryItem({ category, onClick }) {
  return (
    <div
      className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
      onClick={() => onClick(category)}
    >
      <div>
        <div className="font-medium text-gray-900">{category.name}</div>
      </div>
    </div>
  );
}

function MobileActionBar({
  onPublish,
  onTogglePreview,
  isPreviewMode,
  isProcessing,
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 sm:hidden z-40">
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="flex items-center gap-2"
          size="sm"
        >
          <Menu size={16} />
          Actions
        </Button>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={onTogglePreview}
            className="flex items-center gap-2"
            size="sm"
          >
            {isPreviewMode ? <EyeOff size={16} /> : <Eye size={16} />}
            {isPreviewMode ? "Edit" : "Preview"}
          </Button>

          <Button
            variant="primary"
            onClick={onPublish}
            disabled={isProcessing}
            className="flex items-center gap-2"
            size="sm"
          >
            <FileText className="text-white w-4 h-4" />
            {isProcessing ? "Publishing..." : "Publish"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function ArticleWriter() {
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [content, setContent] = useState("");
  const [coverImage, setCoverImage] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isImagePickerOpen, setIsImagePickerOpen] = useState(false);
  const [isCategoryPopoverOpen, setIsCategoryPopoverOpen] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleImageSelected = (imageFile) => {
    setCoverImage(imageFile);
  };

  const removeCoverImage = () => {
    setCoverImage(null);
  };

  const handlePublish = async () => {
    if (!title.trim()) {
      errorToast("Please enter a title");
      return;
    }
    if (!content.trim()) {
      errorToast("Please enter some content");
      return;
    }
    if (!selectedCategory) {
      errorToast("Please select a category");
      return;
    }
    if (!coverImage) {
      errorToast("Please Provide Cover Image.");
    }

    setIsProcessing(true);
    try {
      let coverImageUrl = null;

      if (coverImage) {
        const imageData = new FormData();
        imageData.append("image", coverImage);

        coverImageUrl = await handlePostRequest(
          "/auth/upload-image-to-s3",
          imageData,
          {},
          true
        );
      }

      const payload = {
        article_title: title,
        article_subtitle: subtitle,
        content: content,
        cover_image_url: coverImageUrl
          ? coverImageUrl.data.length > 0
            ? coverImageUrl.data[0]
            : ""
          : "",
        created_at: new Date().toISOString(),
      };

      if (selectedCategory.id === "1") {
        await createMarketPulseArticle(payload);
      } else {
        await createGallyoSentimentArticle(payload);
      }
      // Notify user
      successToast("Article published successfully!");

      // Reset form
      setTitle("");
      setSubtitle("");
      setContent("");
      setCoverImage(null);
      setSelectedCategory(null);
    } catch (error) {
      console.error("Error while uploading article: ", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const categoryPopoverContent = (
    <div className="w-80 max-w-[calc(100vw-2rem)] max-h-96 overflow-y-auto">
      {mockCategories.map((category) => (
        <CategoryItem
          key={category.id}
          category={category}
          onClick={(category) => {
            setSelectedCategory(category);
            setIsCategoryPopoverOpen(false);
          }}
        />
      ))}
    </div>
  );

  return (
    <div className="w-full p-3 sm:p-6 pb-20 sm:pb-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 sm:mb-8 gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <FileText className="text-purple-600 w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Write Article
            </h1>
          </div>

          {/* Desktop Actions */}
          <div className="hidden sm:flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => setIsPreviewMode(!isPreviewMode)}
              className="flex items-center gap-2"
            >
              {isPreviewMode ? <EyeOff size={16} /> : <Eye size={16} />}
              {isPreviewMode ? "Edit" : "Preview"}
            </Button>

            <Button
              variant="primary"
              onClick={handlePublish}
              disabled={isProcessing}
              className="flex items-center gap-2"
            >
              <FileText className="text-white w-4 h-4" />
              {isProcessing ? "Publishing..." : "Publish"}
            </Button>
          </div>
        </div>

        {!isPreviewMode ? (
          <>
            {/* Cover Image Section */}
            <div className="mb-6 sm:mb-8">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Cover Image
              </label>
              {coverImage ? (
                <div className="relative group">
                  <img
                    src={URL.createObjectURL(coverImage) || "/placeholder.svg"}
                    alt="Cover"
                    className="w-full h-48 sm:h-64 object-cover rounded-lg"
                  />
                  <button
                    onClick={removeCoverImage}
                    className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm rounded-full p-2 hover:bg-white transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 sm:p-12 text-center hover:border-gray-400 transition-colors">
                  <div className="flex flex-col items-center gap-4">
                    <div className="p-3 bg-gray-100 rounded-full">
                      <ImageIcon size={32} className="text-gray-400" />
                    </div>
                    <div>
                      <p className="text-gray-600 font-medium mb-1 text-sm sm:text-base">
                        Add a cover image to your article
                      </p>
                      <p className="text-xs sm:text-sm text-gray-500">
                        Drag and drop or click to upload
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
                      <Button
                        variant="outline"
                        onClick={() => setIsImagePickerOpen(true)}
                        className="flex items-center gap-2 justify-center"
                        size="sm"
                      >
                        <Camera size={16} />
                        Take Photo
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setIsImagePickerOpen(true)}
                        className="flex items-center gap-2 justify-center"
                        size="sm"
                      >
                        <ImageIcon size={16} />
                        Upload Image
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Article Meta Information */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6 sm:mb-8">
              <div className="sm:col-span-2 lg:col-span-1">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Sub Category
                </label>
                <Popover
                  content={categoryPopoverContent}
                  open={isCategoryPopoverOpen}
                  onOpenChange={setIsCategoryPopoverOpen}
                >
                  <Button
                    variant="outline"
                    className="w-full justify-between bg-amber-50 border-amber-200 text-gray-700 hover:bg-amber-100"
                    onClick={() => setIsCategoryPopoverOpen(true)}
                  >
                    <div className="flex items-center gap-2">
                      <span className="truncate">
                        {selectedCategory
                          ? selectedCategory.name
                          : "Select Category"}
                      </span>
                    </div>
                    <ChevronDown size={16} className="flex-shrink-0" />
                  </Button>
                </Popover>
              </div>
            </div>

            {/* Selected Category and Tags Display */}
            {selectedCategory && (
              <div className="flex flex-wrap gap-2 mb-6 sm:mb-8">
                {selectedCategory && (
                  <span className="px-3 py-1.5 rounded-full text-sm font-medium bg-amber-100 text-amber-800 flex items-center gap-2">
                    <span className="truncate max-w-[150px]">
                      {selectedCategory.name}
                    </span>
                    <X
                      size={14}
                      className="cursor-pointer hover:text-amber-900 flex-shrink-0"
                      onClick={() => setSelectedCategory(null)}
                    />
                  </span>
                )}
              </div>
            )}

            {/* Title Input */}
            <div className="mb-4 sm:mb-6">
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Article Title"
                className="w-full text-2xl sm:text-3xl lg:text-4xl font-bold border-none outline-none placeholder-gray-400 focus:ring-0 bg-transparent"
              />
            </div>

            {/* Subtitle Input */}
            <div className="mb-6 sm:mb-8">
              <input
                type="text"
                value={subtitle}
                onChange={(e) => setSubtitle(e.target.value)}
                placeholder="Article Subtitle (Optional)"
                className="w-full text-lg sm:text-xl text-gray-600 border-none outline-none placeholder-gray-400 focus:ring-0 bg-transparent"
              />
            </div>

            {/* Rich Text Editor */}
            <div className="mb-6 sm:mb-8">
              <RichTextEditor
                value={content}
                onChange={setContent}
                placeholder="Start writing your article..."
              />
            </div>
          </>
        ) : (
          /* Preview Mode */
          <div className="prose prose-sm sm:prose-lg max-w-none">
            {coverImage && (
              <img
                src={URL.createObjectURL(coverImage) || "/placeholder.svg"}
                alt="Cover"
                className="w-full h-48 sm:h-80 object-cover rounded-lg mb-6 sm:mb-8"
              />
            )}

            <div className="mb-6 sm:mb-8">
              <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-sm text-gray-500 mb-4 sm:mb-6">
                {selectedCategory && (
                  <span className="flex items-center gap-2">
                    <FileText className="text-purple-600 w-4 h-4" />
                    {selectedCategory.name}
                  </span>
                )}
              </div>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 text-gray-900 leading-tight">
              {title || "Article Title"}
            </h1>

            {subtitle && (
              <p className="text-lg sm:text-xl lg:text-2xl text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                {subtitle}
              </p>
            )}

            <div
              className="prose prose-sm sm:prose-lg max-w-none"
              dangerouslySetInnerHTML={{
                __html:
                  content ||
                  "<p class='text-gray-500'>Start writing your article...</p>",
              }}
            />
          </div>
        )}
      </div>

      {/* Mobile Action Bar */}
      <MobileActionBar
        onPublish={handlePublish}
        onTogglePreview={() => setIsPreviewMode(!isPreviewMode)}
        isPreviewMode={isPreviewMode}
        isProcessing={isProcessing}
      />

      <ImagePickerModal
        isOpen={isImagePickerOpen}
        onClose={() => setIsImagePickerOpen(false)}
        onImageSelected={handleImageSelected}
      />
    </div>
  );
}
